import React, { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import pb from '@/lib/pocketbase'
import { useAuth } from '@/hooks/useAuth'
import { useUpdatePeserta } from '@/hooks/useKegiatan'
import type { KegiatanPeserta } from '@/types/pocketbase'
import Card from '@/components/ui/Card'
import Badge from '@/components/ui/Badge'
import Button from '@/components/ui/Button'
import Select from '@/components/ui/Select'
import EmptyState from '@/components/ui/EmptyState'

const paymentOptions = [
  { label: 'Belum Bayar', value: 'belum_bayar' },
  { label: 'Menunggu Verifikasi', value: 'menunggu_verifikasi' },
  { label: 'Lunas', value: 'lunas' },
  { label: 'Batal', value: 'batal' },
]

export default function PesertaDetailPage() {
  const { id: kegiatanId, pesertaId } = useParams<{ id: string; pesertaId: string }>()
  const navigate = useNavigate()
  const { isSuperAdmin } = useAuth()
  const updateMutation = useUpdatePeserta()
  const [editing, setEditing] = useState(false)
  const [paymentStatus, setPaymentStatus] = useState('')

  const { data: peserta, isLoading } = useQuery({
    queryKey: ['peserta', pesertaId],
    queryFn: () => pb.collection('kegiatan_peserta').getOne(pesertaId!),
    enabled: !!pesertaId,
  }) as { data: KegiatanPeserta | undefined; isLoading: boolean }

  if (isLoading) {
    return (
      <div className="py-4 space-y-3">
        <div className="bg-white rounded-card h-48 animate-pulse" />
      </div>
    )
  }

  if (!peserta) {
    return (
      <div className="py-4">
        <EmptyState
          title="Peserta tidak ditemukan"
          action={{ label: 'Kembali', onClick: () => navigate(-1) }}
        />
      </div>
    )
  }

  const handlePaymentUpdate = async () => {
    if (!pesertaId || !kegiatanId || !paymentStatus) return
    try {
      await updateMutation.mutateAsync({
        id: pesertaId,
        data: { status_pembayaran: paymentStatus as typeof peserta.status_pembayaran },
        kegiatanId,
      })
      setEditing(false)
    } catch (err) {
      console.error('Update error:', err)
    }
  }

  const paymentVariant: Record<string, 'green' | 'yellow' | 'red' | 'gray'> = {
    lunas: 'green',
    menunggu_verifikasi: 'yellow',
    belum_bayar: 'red',
    batal: 'gray',
  }

  const paymentLabel: Record<string, string> = {
    lunas: 'Lunas',
    menunggu_verifikasi: 'Menunggu',
    belum_bayar: 'Belum Bayar',
    batal: 'Batal',
  }

  return (
    <div className="py-4 space-y-4">
      <Card>
        <div className="flex items-center gap-4 mb-4">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
            <span className="text-xl font-bold text-primary">
              {(peserta.nama || '?').slice(0, 2).toUpperCase()}
            </span>
          </div>
          <div>
            <h2 className="text-lg font-bold">{peserta.nama}</h2>
            {peserta.nbm && <p className="text-sm text-text-secondary">NBM: {peserta.nbm}</p>}
            {peserta.email && <p className="text-sm text-text-secondary">{peserta.email}</p>}
            {peserta.telepon && <p className="text-sm text-text-secondary">{peserta.telepon}</p>}
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-sm text-text-secondary">Status Pendaftaran</span>
            <Badge variant={peserta.status_pendaftaran === 'diterima' ? 'green' : 'yellow'}>
              {peserta.status_pendaftaran}
            </Badge>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-text-secondary">Status Pembayaran</span>
            {editing && isSuperAdmin ? (
              <div className="flex gap-2">
                <Select
                  options={paymentOptions}
                  value={paymentStatus || peserta.status_pembayaran}
                  onChange={(e) => setPaymentStatus(e.target.value)}
                  className="!py-1 !px-2 text-xs w-40"
                />
                <Button size="sm" onClick={handlePaymentUpdate} loading={updateMutation.isPending}>
                  Simpan
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
                  Batal
                </Button>
              </div>
            ) : (
              <Badge
                variant={paymentVariant[peserta.status_pembayaran]}
                className="cursor-pointer"
                onClick={() => {
                  if (isSuperAdmin) {
                    setPaymentStatus(peserta.status_pembayaran)
                    setEditing(true)
                  }
                }}
              >
                {paymentLabel[peserta.status_pembayaran]}
              </Badge>
            )}
          </div>
        </div>
      </Card>

      <Card>
        <h3 className="text-sm font-semibold mb-3">Informasi Daerah</h3>
        <div className="space-y-2 text-sm">
          {peserta.daerah_id && (
            <div className="flex justify-between">
              <span className="text-text-secondary">PDA</span>
              <span>{peserta.daerah_id}</span>
            </div>
          )}
          {peserta.pimpinan_daerah_id && (
            <div className="flex justify-between">
              <span className="text-text-secondary">Pimpinan Daerah</span>
              <span>{peserta.pimpinan_daerah_id}</span>
            </div>
          )}
          {peserta.jabatan_id && (
            <div className="flex justify-between">
              <span className="text-text-secondary">Jabatan</span>
              <span>{peserta.jabatan_id}</span>
            </div>
          )}
        </div>
      </Card>

      <Button
        block
        variant="outline"
        onClick={() => navigate(`/kegiatan/${kegiatanId}/presensi`)}
      >
        Kembali ke Presensi
      </Button>
    </div>
  )
}
